/**
 * MechanicSlot — chrome wrapper around any mechanic body.
 *
 * Three slot variants:
 *   - marquee: primary slot, full chrome (brand band + body + foot CTA)
 *   - tile:    compact secondary slot (brand mark + mechanic mini-viz + chevron)
 *   - browse:  catalog slot (slightly tightened marquee)
 *
 * Brand chrome wraps without replacing the mechanic body. The mechanic
 * component (MechanicJourney / MechanicLeaderboard / etc.) renders inside
 * `children`. The same mechanic can carry any sponsor — the wrap is
 * configurable per render.
 */

import React from "react";
import "./MechanicSlot.scss";

/*
 * Restrict accepted ctaHref values to http(s) absolute URLs OR a fragment
 * link (e.g. "#" for placeholder slots). Rejects javascript:, data:, and
 * anything unparseable. Mirrors the SocialLinks isValidUrl pattern.
 */
function isHttpOrFragment(url: string | undefined): url is string {
    if (!url) return false;
    if (url === "#" || url.startsWith("#")) return true;
    try {
        const u = new URL(url, window.location.origin);
        return u.protocol === "https:" || u.protocol === "http:";
    } catch {
        return false;
    }
}

export type MechanicSlotVariant = "marquee" | "tile" | "browse";

export interface MechanicBrandChrome {
    /** Brand id used to scope the gradient (chipotle / scuf / gfuel / monster / redbull / mms / generic). */
    brandKey: string;
    /** Short brand name shown in the chrome band (e.g. "Chipotle", "SCUF Gaming"). */
    brandName: string;
    /**
     * Mark rendered in the tile-state square. Either a short string
     * (defaults to the first letter of brandName when omitted) or a React
     * node — typically a small icon (e.g. `<FaTrophy />`) for placeholder
     * slots that don't have a real sponsor brand attached yet.
     */
    brandMark?: React.ReactNode;
    /** Display title of the activation (e.g. "Unlock the Burrito Vault"). */
    title: string;
    /** Status pill copy — typically a live indicator or time-left string. */
    status?: string;
    /** Whether the status pill renders a pulsing green dot. */
    isLive?: boolean;
    /** Reward copy shown in the marquee/browse footer (e.g. "Free Burrito + 500 XP"). */
    reward?: string;
    /** CTA button label. */
    ctaLabel?: string;
    /** CTA click — when omitted the CTA renders as a link target via `ctaHref`. */
    onCtaClick?: () => void;
    /** CTA href fallback (used for tile chevron link target as well). */
    ctaHref?: string;
}

interface MechanicSlotProps extends MechanicBrandChrome {
    variant: MechanicSlotVariant;
    /** The mechanic-specific body — journey path, leaderboard rows, bingo grid, etc. */
    children: React.ReactNode;
    /** Optional override for the mechanic type label rendered under the brand name in tile state. */
    mechanicTypeLabel?: string;
}

export const MechanicSlot: React.FC<MechanicSlotProps> = ({
    variant,
    brandKey,
    brandName,
    brandMark,
    title,
    status,
    isLive,
    reward,
    ctaLabel,
    onCtaClick,
    ctaHref,
    mechanicTypeLabel,
    children,
}) => {
    const classBrand = `mechanic-slot__band--${brandKey}`;
    const classMark = `mechanic-slot__mark--${brandKey}`;

    /*
     * Validate any caller-supplied ctaHref before passing it to an <a>
     * href. The slot's chrome props are an open public interface — future
     * callers (including admin-authored challenge configs) might populate
     * with arbitrary values. javascript:/data: URIs in href execute in
     * browser context when clicked.
     */
    const safeCtaHref = isHttpOrFragment(ctaHref) ? ctaHref : undefined;

    if (variant === "tile") {
        const tileInner =
            <>
                <span className={`mechanic-slot__mark ${classMark}`}>
                    {brandMark != null ? brandMark : brandName.charAt(0).toUpperCase()}
                </span>
                <div className="mechanic-slot__tile-meta">
                    <span className="mechanic-slot__tile-name">{brandName}</span>
                    <div className="mechanic-slot__tile-viz">
                        {children}
                        {mechanicTypeLabel && <span className="mechanic-slot__tile-type">{mechanicTypeLabel}</span>}
                    </div>
                </div>
                <span className="mechanic-slot__chev" aria-hidden="true">›</span>
            </>;

        /*
         * The whole tile is the click target, so it MUST be a real control. An
         * <a> without an href is neither focusable nor announced as a link —
         * rendering one whenever ctaHref is absent or rejected made the entire
         * tile mouse-only, which is every sponsor tile authored without a CTA
         * link. Link when we have somewhere to go, button when we only have a
         * handler.
         */
        if (safeCtaHref) {
            return (
                <a
                    className="mechanic-slot mechanic-slot--tile"
                    href={safeCtaHref}
                    onClick={onCtaClick}
                    title={title}
                >
                    {tileInner}
                </a>
            );
        }
        return (
            <button
                type="button"
                className="mechanic-slot mechanic-slot--tile"
                onClick={onCtaClick}
                title={title}
            >
                {tileInner}
            </button>
        );
    }

    const isBrowse = variant === "browse";
    return (
        <div className={`mechanic-slot mechanic-slot--${variant}`}>
            <div className={`mechanic-slot__band ${classBrand}`}>
                <span className="mechanic-slot__band-hatch" aria-hidden="true" />
                <div className="mechanic-slot__band-row">
                    <span className="mechanic-slot__brand-name">{brandName}</span>
                    {status &&
                        <span className={`mechanic-slot__status${isLive ? " is-live" : ""}`}>
                            {status}
                        </span>
                    }
                </div>
                <h3 className="mechanic-slot__title">{title}</h3>
            </div>
            <div className={`mechanic-slot__body${isBrowse ? " is-browse" : ""}`}>
                {children}
            </div>
            {(reward || ctaLabel) &&
                <footer className="mechanic-slot__foot">
                    {reward && <span className="mechanic-slot__reward">{reward}</span>}
                    {/* Same rule as the tile — a hrefless <a> can't be reached by keyboard. */}
                    {ctaLabel && (safeCtaHref
                        ? <a
                            className="mechanic-slot__cta"
                            href={safeCtaHref}
                            onClick={onCtaClick}
                        >
                            {ctaLabel}
                        </a>
                        : <button
                            type="button"
                            className="mechanic-slot__cta"
                            onClick={onCtaClick}
                        >
                            {ctaLabel}
                        </button>)
                    }
                </footer>
            }
        </div>
    );
};
